import { takeLatest } from 'redux-saga/effects';

import { environment } from 'environments/environment';
import { AppAction, createActionCreator, createActionType } from 'helpers/redux/reduxHelpers';
import {
  DEEPLINK_COMMAND_PORTLET_MAPPINGS,
  DeeplinkCommandPortletMapping,
  LegacyCrmDeeplinkCommand,
  LegacyCrmDeeplinkConfig,
} from 'models/LegacyCrmDeeplink';
import { selectProperty } from 'saga/property/ducks';

const LOG_TO_VISITED_HISTORY_PARAM = 'logToVisitedHistory';
const LOG_TO_VISITED_HISTORY_ALLOWED_COMMANDS: LegacyCrmDeeplinkCommand[] = [
  LegacyCrmDeeplinkCommand.EMO2DASH,
  LegacyCrmDeeplinkCommand.IMO2DASH,
  LegacyCrmDeeplinkCommand.IMO2ACCESS,
  LegacyCrmDeeplinkCommand.EMO2ACCESS,
  LegacyCrmDeeplinkCommand.IMO2BILL,
  LegacyCrmDeeplinkCommand.EMO2BILL,
  LegacyCrmDeeplinkCommand.IMO2NEWS,
  LegacyCrmDeeplinkCommand.EMO2NEWS,
  LegacyCrmDeeplinkCommand.EMO2CUSELECT,
];

const getNamespacedParameter = (command: LegacyCrmDeeplinkCommand, paramName: string): string => {
  const mapping: DeeplinkCommandPortletMapping | undefined = DEEPLINK_COMMAND_PORTLET_MAPPINGS.find(
    (m: DeeplinkCommandPortletMapping) => m.command === command
  );

  return mapping ? `_${mapping.portlet}_WAR_customercareportlets_${paramName}` : paramName;
};

/* STATE */

/* ACTION TYPES */

export enum CrmDeeplinkActionTypes {
  RedirectToLegacyCrm = '@@CrmDeeplink/REDIRECT_TO_LEGACY_CRM',
}

/* ACTIONS */
export const redirectToLegacyCrmAction = createActionCreator(CrmDeeplinkActionTypes.RedirectToLegacyCrm);

/* SAGAS */
/**
 * Redirects to legacy CRM application via deeplink.
 * Builds a deeplink URL for redirection to legacy CRM application (see gui_common.docx, chapter 'Deeplinks').
 * @param payload payload of type {@link LegacyCrmDeeplinkConfig}
 */
function redirectToLegacyCrm({ payload }: AppAction<LegacyCrmDeeplinkConfig>) {
  const command: LegacyCrmDeeplinkCommand = payload.command;
  const queryParams: Record<string, string | undefined> = payload.queryParams || {};

  const deeplinkBaseUrl: string = selectProperty(environment.urlConfig.oneportalDeeplinker.propertyKey);

  window.location.href = `${deeplinkBaseUrl || '/'}${buildLegacyCrmDeeplinkParams(command, queryParams)}`;
}

/* EXPORT */
export function* crmDeeplinkSaga() {
  yield takeLatest(createActionType(CrmDeeplinkActionTypes.RedirectToLegacyCrm), redirectToLegacyCrm);
}

export const buildLegacyCrmDeeplinkParams = (
  command: LegacyCrmDeeplinkCommand,
  queryParams: Record<string, string | undefined>
): string => {
  if (!command) {
    throw Error('Unable to generate deeplink to legacy CRM: deeplink "command" is missing.');
  } else if (
    // add logToVisitedHistory parameter if command is in LOG_TO_VISITED_HISTORY_ALLOWED_COMMANDS and it is not already present in query params
    LOG_TO_VISITED_HISTORY_ALLOWED_COMMANDS.some((c: LegacyCrmDeeplinkCommand) => c === command) &&
    !Object.keys(queryParams).some((p: string) => p === LOG_TO_VISITED_HISTORY_PARAM)
  ) {
    queryParams = { ...queryParams };
    queryParams[getNamespacedParameter(command, LOG_TO_VISITED_HISTORY_PARAM)] = 'true';
  }

  const serializedQueryParams = Object.keys(queryParams)
    .map((paramName: string) => `${paramName}=${queryParams[paramName]}`)
    .join('&');

  return `?command=${command}&${serializedQueryParams}`;
};
